/* eslint-disable camelcase */
import { LTPRoles, UserRoles } from "@constants/UserRoles";
import Utils from "neon-frontend-utils/src/utils";
import canViewOrganisationSettingsTabs from "./constants/canViewOrganisationSettingsTabs";
import SupportedLanguages from "./constants/SupportedLanguages";
import Endpoints from "./Endpoints.mjs";

/**
 * Configuration module
 * Write any (default) configuration here
 * @type {{}}
 */
const AppConfig = {
    analytics: {
        environments: ["staging"],
        plugins: {
            mixpanel: {
                token: `${process.env.MIXPANEL_TOKEN}`,
            },
        },
    },
    api: {
        neon: {
            baseUrl: `${process.env.NEON_API_BASE_URL}`, // default is dev api, which is set in webpack.config.js
            // allow to use cross-domain cookies for authentication
            // https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
            credentials: "include",

            endpoints: Endpoints,

            globalDateFormat: "YYYY-MM-DD HH:mm",

            isoUTCFormat: "YYYY-MM-DDTHH:mm:ss[Z]",

            loggingExclusions: {
                // endpoints of api errors should not be logged at all
                endpoints: [],

                // header fields from the api call that failed that should be excluded from logging
                headers: [],

                // fields from the post body of an api error that should be excluded from logging
                postBody: ["password"],

                // fields from the response body of an api error that should be excluded from logging
                responseBody: [],
            },

            loginRedirect: "/login",

            logoutRedirect: "/login",
            maintenanceRedirect: "/maintenance",
            requestFailedMessage: "An error occurred while processing your request.",
            skipPrefixIndexParams: true,
            urlEncodeParams: true,
        },
    },
    authenticator: {
        cognito: {
            appClientId: `${process.env.COGNITO_APP_CLIENT_ID}`,
            userPoolId: `${process.env.COGNITO_USER_POOL_ID}`, //  default is dev, which is set in webpack.config.js
        },
        neon: {
            loginRedirect: "/login",
        },
    },
    authoriser: {
        neon: {
            addLtpUser: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
            },
            aiSettings: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS],
            },
            builder: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS],
            },
            cacheManager: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
            },
            exampleReports: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_TENDER_TEAM],
            },
            featureToggles: {
                featureToggleAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
            },
            finance: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_FINANCE],
            },
            financeInvoices: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_FINANCE],
                viewInvoicesAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_FINANCE],
            },
            financePricing: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_FINANCE],
                viewPricingAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_FINANCE],
            },
            header: {
                featureToggleAction: [UserRoles.ROLE_APPLICATION_MANAGERS],
            },
            home: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
            },
            loginRedirect: "/login",
            ltpSettings: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS],
            },
            mask: {
                route: [UserRoles.ROLE_APPLICATION_MANAGERS],
            },
            menu: {
                viewMenu: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_ASSESSORS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_MANAGER,
                    UserRoles.ROLE_RESPONDENT,
                    UserRoles.ROLE_REPORT_RECEIVER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_FINANCE,
                    UserRoles.ROLE_GROUP_REPORT_RECEIVER,
                    UserRoles.ROLE_TENDER_TEAM,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
                viewTour: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_ADMIN],
            },
            organisationSettings: {
                addNewProfileAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                ],
                addProductPreferencesForJobFunction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                addRootTreeNodeAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                addSelfRegistrationProfile: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                addSubOrganisationAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                addTreeNodeAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                addUserAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                adminAmendNameAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                allowAddingClientAdminRoleAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                amendNameAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                amendProgramAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                amendUserAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                canSeeDilemmaTest: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                canUpdateProfileBilling: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_FINANCE,
                ],
                changeScoreSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                deactivateJobFunctionAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                deactivateOrganisationAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                deactivateUserAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                deleteRoleFromUser: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                editCompetencySelectionAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CONSULTANTS,
                ],
                editEmailsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                editOrganisationBasicSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                editProductSelectionAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                editProjectBasicSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                editSelfRegistrationSettings: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                exportApiSettings: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                exportUsersAction: [UserRoles.ROLE_APPLICATION_MANAGERS],
                forceEntraRegisterAction: [UserRoles.ROLE_APPLICATION_MANAGERS],
                getApiSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                getBillingAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_FINANCE,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                getContactsAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                getProfileBillingAction: LTPRoles,
                getScoreSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                getSelfRegistrationAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                getTreeNodesAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_FINANCE,
                ],
                inviteUserAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                manageOrganisationProductPriceAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_FINANCE,
                ],
                masqueradeAsUserAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                profileAliasAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_FINANCE,
                ],
                selectAuthenticationProvider: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                toggleMandatoryReflection: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CONSULTANTS],
                uploadOrganisationLogo: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],

                viewTab_api_settings: canViewOrganisationSettingsTabs,
                viewTab_billing: [UserRoles.ROLE_FINANCE, ...canViewOrganisationSettingsTabs],
                viewTab_competencies: [UserRoles.ROLE_BACKOFFICE, ...canViewOrganisationSettingsTabs],
                viewTab_contactsTab: canViewOrganisationSettingsTabs,
                viewTab_emails: canViewOrganisationSettingsTabs,
                viewTab_organisationBasicSettings: canViewOrganisationSettingsTabs,
                viewTab_products: canViewOrganisationSettingsTabs,
                viewTab_profileProductsTab: [UserRoles.ROLE_BACKOFFICE, ...canViewOrganisationSettingsTabs],
                viewTab_projectBasicSettings: canViewOrganisationSettingsTabs,
                viewTab_score_settings: canViewOrganisationSettingsTabs,
                viewTab_selfRegistrationProductsTab: canViewOrganisationSettingsTabs,
                viewTab_users: canViewOrganisationSettingsTabs,
            },
            organisationSettingsAddUser: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
            },
            participants: {
                accessPlanningPage: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BACKOFFICE,
                ],
                alwaysAmendCompetenciesAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CONSULTANTS,
                ],
                alwaysShowReportButtonAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BACKOFFICE,
                ],
                amendAppointmentSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                ],
                anonymizeAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                canMarkAsPresent: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                canSeeDashboards: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                canSeeDilemmaTest: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                canSeePlanningFilter: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_BACKOFFICE,
                ],
                canSeeProjectSubtitle: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                ],
                copyReport: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_TENDER_TEAM],
                deleteParticipantAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_OFFICE_MANAGER,
                ],
                filterByAppointmentDates: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                importParticipantsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                inviteParticipantAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                masqueradeAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                moveParticipantsIntoPhase: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                moveProjectAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                openDetailPanelAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                ],
                overrideDecisionPhase2Action: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                requestAssessmentAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                resetQuestionnaireAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                resultsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
                retriggerScoresAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_ASSESSORS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_MANAGER,
                    UserRoles.ROLE_RESPONDENT,
                    UserRoles.ROLE_REPORT_RECEIVER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_TENDER_TEAM,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
                sendReminderAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                setDefaultAppointmentDate: [UserRoles.ROLE_ASSESSMENT_TEAM],
                showReportButtonAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_REPORT_RECEIVER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_TENDER_TEAM,
                ],
                swapProgramItemAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                switchAssessmentGoal: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                switchLiveProductAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                toggleAbilityTestsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                toggleAdjustedProgramAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                toggleProgramItemAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                updateInterpretationAfterCompletion: [UserRoles.ROLE_FINANCE],
                viewClientInfoAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                viewDisabledProjectAction: [UserRoles.ROLE_APPLICATION_MANAGERS],
                viewReportAndResultInternal: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_REPORT_RECEIVER],
            },
            participantsDetail: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_ASSESSORS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
            },
            participantsRequestAssessment: {
                amendAppointmentSettingsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                canSeeTeamAndOrganisationProduct: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                deleteImportedParticipantAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
                hideLiveProducts: [UserRoles.ROLE_CLIENT_ADMIN, UserRoles.ROLE_OFFICE_MANAGER],
                importParticipantsAction: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CLIENT_ADMIN,
                ],
                toggleDemoParticipant: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
            },
            planningDashboard: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                ],
            },
            profile: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_OFFICE_MANAGER_NO_REPORT,
                    UserRoles.ROLE_REPORT_RECEIVER,
                    UserRoles.ROLE_FINANCE,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_GROUP_REPORT_RECEIVER,
                    UserRoles.ROLE_TENDER_TEAM,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
            },
            report: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_REPORT_RECEIVER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                    UserRoles.ROLE_TENDER_TEAM,
                ],
                showDebuggerAction: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_TENDER_TEAM],
                writeAction: [
                    // legacy
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BACKOFFICE,
                ],
                writeReportAction: [
                    // live products, hybrid
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                ],
            },
            results: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BACKOFFICE,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_ASSESSMENT_TEAM,
                ],
            },
            talentAnalytics: {
                canFetchAvailableUsers: [
                    UserRoles.ROLE_CLIENT_ADMIN,

                    // will return empty array for LTP users
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                ],
                canManageAll: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                createGroupReport: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_GROUP_REPORT_RECEIVER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
            },
            talentAnalyticsDetail: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_ASSOCIATES,
                    UserRoles.ROLE_CLIENT_ADMIN,
                    UserRoles.ROLE_OFFICE_MANAGER,
                    UserRoles.ROLE_GROUP_REPORT_RECEIVER,
                    UserRoles.ROLE_BEDRIJFSBUREAU,
                ],
            },
            talentAnalyticsSettings: {
                canEdit: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
            },
            teamReport: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
            },
            teams: {
                canSelectOrganisation: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
            },
            teamSettings: {
                route: [
                    UserRoles.ROLE_APPLICATION_MANAGERS,
                    UserRoles.ROLE_CONSULTANTS,
                    UserRoles.ROLE_CLIENT_CENTER,
                    UserRoles.ROLE_TEAM_ADMIN,
                    UserRoles.ROLE_TEAM_MANAGER,
                ],
            },
            tenantSelectionRedirect: "/login/select-organisation",
            users: {
                addUser: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                inviteUser: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                route: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
                showDeactivated: [UserRoles.ROLE_APPLICATION_MANAGERS, UserRoles.ROLE_CLIENT_CENTER],
            },
        },
    },
    global: {
        alertTimeout: 5000,
        assessmentReportScorePollingTimeout: 10000,
        forgotPasswordUrl: "/forgot-password",
        s3Bucket: "https://s3.eu-central-1.amazonaws.com/assessment-tools-images",
    },
    languages: {
        defaultLanguageId: Utils.getBrowserLanguage(SupportedLanguages, "nl_NL", {
            // eslint-disable-line
            "de-de": "de_DE",

            en: "en_GB",

            en_gb: "en_GB",

            en_us: "en_GB",

            // eslint-disable-line
            "en-gb": "en_GB",

            // eslint-disable-line
            "en-us": "en_GB",

            nl: "nl_NL",

            nl_nl: "nl_NL",

            "nl-nl": "nl_NL",
            de_de: "de_DE", // eslint-disable-line
            "fr-fr": "fr_FR",
            fr_fr: "fr_FR", // eslint-disable-line
        }),
        supported: SupportedLanguages,
    },
    logger: {
        application: "customer-portal",
        baseUrl: `${process.env.NEON_API_BASE_URL}`,
        devMode: false,
        devModeBaseUrl: "http://localhost:19270",
        fetchTokenUrl: `${process.env.NEON_API_BASE_URL}/api/v1/logzio/key`,
        listenerUrl: "https://listener-eu.logz.io",
    },
    menuItems: [
        {
            component: "ltpSettings",
            iconType: null,
            label: "ltp_settings",
            link: "/ltpSettings",
        },
        {
            component: "home",
            iconType: "home",
            label: "home",
            link: "/home",
        },
        {
            component: "participants",
            iconType: "user",
            label: "participants",
            link: "/participants",
        },
        {
            component: "teams",
            featureFlag: "teams",
            iconType: "users",
            label: "teams",
            link: "/teams",
        },
        {
            component: "talentAnalytics",
            iconType: "chart-column",
            label: "talent_analytics",
            link: "/talent-analytics",
        },
        {
            component: "organisationSettings",
            iconType: "city",
            label: "company_settings",
            link: "/organisation-settings",
        },
        {
            component: "users",
            iconType: "users-cog",
            label: "users",
            link: "/users",
        },
        {
            component: "finance",
            iconType: "file-invoice-dollar",
            label: "finance",
            link: "/finance",
        },
        {
            component: "exampleReports",
            iconType: "file-signature",
            label: "example_reports",
            link: "/example-reports",
        },
    ],
    organisationSettings: {
        participantPortalBaseUrl: `${process.env.PARTICIPANT_PORTAL_BASE_URL}`,
    },
    pageTitle: "LTP Neon",
    portalName: "client-portal",
    report: {
        downloadOldIntermediateReportAsPdf: "/api/v1/download-intermediate-report/{slug}",
        textSaveInterval: 3000,
    },
    routes: {
        addLtpUser: "/users/add-user",
        assessmentExampleReport: "/example-report",
        assessmentreport: "/report",
        createReservation: "/participants/create-reservation",
        defaultRoute: "/",
        exampleReports: "/example-reports",
        finance: "/finance",
        forgotPassword: "/forgot-password",
        loginRoute: "/login",
        organisationSettings: "/organisation-settings",
        organisationSettingsAddUser: "/organisation-settings/add-user",
        participants: "/participants",
        participantsDetail: "/participant/:id",
        participantsRequestAssessmentV2: "/participants/request-assessment-v2",
        profile: "/profile",
        results: "/results",
        selectOrganisation: "/login/select-organisation",
        talentAnalytics: "/talent-analytics",
        talentAnalyticsDetail: "/talent-analytics/:id",
        talentAnalyticsSettings: "/talent-analytics/:id/settings",
        teamReport: "/teams/:id/report",
        teams: "/teams",
        users: "/users",
        website: {
            aiTool: "https://assessment.schrijfevenmee.nl",
            eLearning: "https://ltp.nl/e-learning-interpretatie-online-assessments-ltp/",
            faq: {
                en: "https://ltp.nl/en/frequently-asked-questions/",
                nl: "https://ltp.nl/veelgestelde-vragen/",
            },
            ltpServices: {
                en: "https://ltp.nl/en/services/",
                nl: "https://ltp.nl/diensten/",
            },
            quickStart: "https://ltp.nl/quickstart-assessmentportal-ltp/",
            releaseNotes: "https://updates.ltponline.com/",
        },
    },
    sentry: {
        developDsn: "https://b4ab566b94f442f2b04e0bbfa13b69b7@o1351458.ingest.sentry.io/6634539",
        dsn: `${process.env.SENTRY_DSN}`,
        environments: ["staging", "production"],
        sampleRate: process.env.TARGET_ENV === "production" ? 0.01 : 0.05,
    },
    sources: {
        froala: "/assets/vendor/froala-editor/js/froala_editor.min.js",
        froalaAlignPlugin: "/assets/vendor/froala-editor/js/align.min.js",
        froalaCodeViewPlugin: "/assets/vendor/froala-editor/js/code_view.min.js",
        froalaImagePlugin: "/assets/vendor/froala-editor/js/image.min.js",
        froalaInlineClassPlugin: "/assets/vendor/froala-editor/js/inline_class.min.js",
        froalaLinkPlugin: "/assets/vendor/froala-editor/js/link.min.js",
        froalaListPlugin: "/assets/vendor/froala-editor/js/lists.min.js",
        froalaParagraphPlugin: "/assets/vendor/froala-editor/js/paragraph_format.min.js",
        froalaTablePlugin: "/assets/vendor/froala-editor/js/table.min.js",
        jquery: "/assets/vendor/froala-editor/js/jquery.slim.min.js",
    },
    subMenuItems: [
        {
            component: "builder",
            iconType: "hammer",
            label: "builder",
            link: "/ltpSettings/builder",
            parent: "ltpSettings",
        },
        {
            component: "aiSettings",
            iconType: "hat-wizard",
            label: "ai-settings",
            link: "/ltpSettings/ai-settings",
            parent: "ltpSettings",
        },
    ],
};

export default AppConfig;
